import { Button } from '@ai-tutor/ui';
import { Link as LinkIcon, ExternalLink } from 'lucide-react';
import type { Source } from '@/data/fixtures';
import { resolveEmbed } from '@/components/features/learning/sources/embedUrl';
import { ViewerShell } from '@/components/features/learning/sources/viewers/shell/ViewerShell';

interface LinkViewerProps {
  source: Source;
  onOpenExternal: (url: string) => void;
}

/**
 * Two render modes:
 *  - **Embeddable URL** (YouTube, Vimeo) → iframe player taking the
 *    full panel, with the shell toolbar for open-externally + fullscreen.
 *  - **Anything else** → a centered card with the URL + "Open in new
 *    tab" CTA. We don't try to iframe arbitrary sites because nearly
 *    all set `X-Frame-Options: DENY` and the iframe just renders a
 *    blank "refused to connect" page.
 */
export function LinkViewer({ source, onOpenExternal }: LinkViewerProps) {
  const embed = source.url ? resolveEmbed(source.url) : null;

  if (embed) {
    return (
      <ViewerShell
        externalUrl={source.url}
        fullscreen
        icon={LinkIcon}
        label={embed.provider === 'youtube' ? 'YouTube' : 'Vimeo'}
        tone='lesson'
      >
        <div className='flex h-full items-center justify-center p-6'>
          <div className='aspect-video max-h-full w-full max-w-4xl overflow-hidden rounded-xl bg-black shadow-xl ring-1 ring-border'>
            <iframe
              title={source.name}
              src={embed.embedUrl}
              allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
              allowFullScreen
              // YouTube/Vimeo set their own frame-ancestors so we don't
              // need `allow-same-origin`. No `allow-scripts` either -
              // the iframe origin handles its own JS.
              className='h-full w-full border-0'
            />
          </div>
        </div>
      </ViewerShell>
    );
  }

  // Fallback for non-embeddable links - clean card, no fake "preview
  // will load here" empty box.
  return (
    <ViewerShell icon={LinkIcon} label='Link' tone='lesson'>
      <div className='flex h-full items-center justify-center p-6'>
        <div className='w-full max-w-md rounded-2xl border border-border/60 p-8 text-center'>
          <div className='mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-lesson/10'>
            <LinkIcon className='h-7 w-7 text-lesson' />
          </div>
          <h3 className='mb-1 text-lg font-semibold text-foreground'>
            {source.name}
          </h3>
          {source.url && (
            <p className='mb-6 break-all text-xs text-muted-foreground'>
              {source.url}
            </p>
          )}
          {source.url && (
            <Button
              onClick={() => onOpenExternal(source.url!)}
              className='tap-target'
            >
              <ExternalLink className='mr-2 h-4 w-4' />
              Open in new tab
            </Button>
          )}
          <p className='mt-4 text-xs text-muted-foreground'>
            Most websites block embedding. Open in a new tab to view.
          </p>
        </div>
      </div>
    </ViewerShell>
  );
}
